// React and Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// API and Services
import { getTollAPI } from "services/api/calculations/toll/api";
import { initialTollDetailForm } from "services/api/calculations/toll/consts";

// Types
import { TollDetailFormValues } from "services/api/calculations/toll/types";
import { Errors } from "services/forms/validation/types";
import { TollDetailPageCmp, TollDetailPageProps } from "./types";

// UI Components
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { Button } from "ui/components/Button/Button";

// Cards and Components
import TollDetailCard from "./components/TollDetailCard/TollDetailCard";
import TollDeleteCard from "./components/TollDeleteCard/TollDeleteCard";

// Project Constants & Helpers
import { CALCULATION_TOLL_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_TOLL } from "app/components/Sidebar/consts";
import { transformRoute, useTranslate } from "helpers";
import { Routes } from "ui/modules/calculation/navigation";

const TollDetailPage: TollDetailPageCmp = (props: TollDetailPageProps) => {
  const { tollId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<TollDetailFormValues>(
    initialTollDetailForm
  );

  const navigate = useNavigate();

  const title = CALCULATION_TOLL_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (tollId) fetchToll(tollId);
  }, [tollId]);

  const fetchToll = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTollDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTollAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_TOLL.text,
      route: SIDEBAR_ITEM_CALCULATION_TOLL.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationTollSearchPageRouter, {
        tollId,
      })
    );
  }, []);

  if (!tollId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white border-b border-slate-200 rounded-t-md">
          <PageHeader>
            <PageTitle
              title={`${title.text}`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children={t("admin.calculation.setting.toll.detail.delete")}
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4 py-6  lg:px-4">
          <TollDetailCard tollId={tollId} />
        </div>
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.toll.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <TollDeleteCard tollId={tollId} onSuccess={handleDeleteSuccess} />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

TollDetailPage.route = Routes.CalculationTollDetailPageRouter;

export default TollDetailPage;
